import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  ChevronRight,
  Loader2,
  MapPin,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import {
  ORG_TYPES,
  createSite,
  deleteOrganization,
  deleteSite,
  fetchOrganizations,
  updateOrganization,
  updateSite,
} from '@/lib/api/spaces.js'
import {
  initialModelForm,
  persistEntityModel,
  pickModelAsset,
} from '@/lib/modelPresets.js'
import ModelFormSection from '@/components/common/ModelFormSection.jsx'
import RowMenu from '@/components/common/RowMenu.jsx'
import TimezoneSelect from '@/components/common/TimezoneSelect.jsx'
import OrgMark from '@/components/common/OrgMark.jsx'

export default function OrganizationDetailPage() {
  const { orgId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: orgs = [], isLoading } = useQuery({
    queryKey: ['orgs'],
    queryFn: fetchOrganizations,
  })

  const org = useMemo(() => orgs.find((o) => o.id === orgId) || null, [orgs, orgId])
  const [dialog, setDialog] = useState(null)
  const invalidateOrgs = () => queryClient.invalidateQueries({ queryKey: ['orgs'] })

  const updateOrgMutation = useMutation({
    mutationFn: ({ id, patch }) => updateOrganization(id, patch),
    onSuccess: () => { invalidateOrgs(); setDialog(null) },
  })
  const deleteOrgMutation = useMutation({
    mutationFn: deleteOrganization,
    onSuccess: () => { invalidateOrgs(); navigate('/organizations') },
  })
  const createSiteMutation = useMutation({
    mutationFn: async ({ orgId, values }) => {
      const { siteModel, ...siteFields } = values
      const site = await createSite(orgId, siteFields)
      await persistEntityModel('site', site.id, null, siteModel)
      return site
    },
    onSuccess: () => { invalidateOrgs(); setDialog(null) },
  })
  const updateSiteMutation = useMutation({
    mutationFn: async ({ siteId, site, values }) => {
      const { siteModel, ...siteFields } = values
      const updated = await updateSite(siteId, siteFields)
      await persistEntityModel('site', siteId, site, siteModel)
      return updated
    },
    onSuccess: () => { invalidateOrgs(); setDialog(null) },
  })
  const deleteSiteMutation = useMutation({
    mutationFn: deleteSite,
    onSuccess: invalidateOrgs,
  })

  const submitting =
    updateOrgMutation.isPending ||
    createSiteMutation.isPending ||
    updateSiteMutation.isPending

  const dialogError =
    updateOrgMutation.error?.uiMessage ||
    createSiteMutation.error?.uiMessage ||
    updateSiteMutation.error?.uiMessage ||
    null

  const deleteError =
    deleteOrgMutation.error?.uiMessage ||
    deleteSiteMutation.error?.uiMessage ||
    null

  const dismissDeleteError = () => {
    deleteOrgMutation.reset()
    deleteSiteMutation.reset()
  }

  const resetMutations = () => {
    updateOrgMutation.reset()
    createSiteMutation.reset()
    updateSiteMutation.reset()
  }

  const handleDeleteOrg = () => {
    if (!org) return
    const sites = (org.sites || []).length
    const floors = countFloors(org)
    const msg =
      sites + floors > 0
        ? `Delete "${org.name}"? This also removes all sites, buildings, and floors inside it.`
        : `Delete "${org.name}"?`
    if (window.confirm(msg)) deleteOrgMutation.mutate(org.id)
  }

  const handleDeleteSite = (site) => {
    if (window.confirm(`Delete "${site.name}"? This also removes all buildings and floors inside it.`)) {
      deleteSiteMutation.mutate(site.id)
    }
  }

  const handleDialogSubmit = (values) => {
    if (dialog.kind === 'org') {
      updateOrgMutation.mutate({ id: org.id, patch: values })
    } else if (dialog.kind === 'site' && dialog.mode === 'create') {
      createSiteMutation.mutate({ orgId: org.id, values })
    } else if (dialog.kind === 'site') {
      updateSiteMutation.mutate({ siteId: dialog.site.id, site: dialog.site, values })
    }
  }

  if (isLoading) {
    return (
      <main className="app-content">
        <div className="muted">Loading…</div>
      </main>
    )
  }

  if (!org) {
    return (
      <main className="app-content">
        <header className="page-header">
          <div>
            <Link to="/organizations" className="row" style={{ gap: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              <ArrowLeft size={14} /> Back to organizations
            </Link>
            <h1 className="page-header__title" style={{ marginTop: 8 }}>Organization not found</h1>
            <div className="page-header__sub">This organization may have been deleted.</div>
          </div>
        </header>
      </main>
    )
  }

  const sites = org.sites || []

  return (
    <main className="app-content">
      <header className="page-header">
        <div>
          <Link to="/organizations" className="row" style={{ gap: 6, fontSize: 13, color: 'var(--text-muted)' }}>
            <ArrowLeft size={14} /> Back to organizations
          </Link>
          <h1 className="page-header__title" style={{ marginTop: 8 }}>{org.name}</h1>
          <div className="page-header__sub">
            <span>{typeLabel(org.type)}</span>
            {' · '}
            <span>{org.slug}</span>
            {' · '}
            <span>{sites.length} sites · {countFloors(org)} floors</span>
          </div>
        </div>
        <div className="row">
          <button
            type="button"
            className="btn"
            onClick={() => setDialog({ kind: 'org', mode: 'edit' })}
          >
            <Pencil size={14} /> Edit organization
          </button>
          <button
            type="button"
            className="btn btn--danger"
            onClick={handleDeleteOrg}
            disabled={deleteOrgMutation.isPending}
          >
            <Trash2 size={14} /> Delete
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setDialog({ kind: 'site', mode: 'create' })}
          >
            <Plus size={14} /> New site
          </button>
        </div>
      </header>

      {deleteError && (
        <div className="auth-error" role="alert" style={{ marginBottom: 12 }}>
          <span>{deleteError}</span>
          <button type="button" className="btn btn--sm" onClick={dismissDeleteError}>
            Dismiss
          </button>
        </div>
      )}

      <section className="card card--lg card--flush">
        {sites.length === 0 ? (
          <div className="table__empty">
            <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>
              No sites yet
            </div>
            <div style={{ marginBottom: 14 }}>Add a site to start mapping spaces for this organization.</div>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => setDialog({ kind: 'site', mode: 'create' })}
            >
              <Plus size={14} /> New site
            </button>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Site</th>
                <th>Address</th>
                <th>Buildings</th>
                <th>Floors</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {sites.map((site) => {
                const open = () => navigate(`/spaces/sites/${site.id}`)
                const onKeyDown = (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    open()
                  }
                }
                return (
                  <tr
                    key={site.id}
                    className="is-interactive"
                    role="link"
                    tabIndex={0}
                    onClick={open}
                    onKeyDown={onKeyDown}
                  >
                    <td>
                      <div className="row">
                        <OrgMark />
                        <div>
                          <div style={{ fontWeight: 600 }}>{site.name}</div>
                          <div className="muted" style={{ fontSize: 12 }}>{site.timezone || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        <MapPin size={14} color="var(--text-muted)" />
                        <span className="muted" style={{ fontSize: 13 }}>
                          {site.address || '—'}
                        </span>
                      </div>
                    </td>
                    <td>{(site.buildings || []).length}</td>
                    <td>{countFloorsInSite(site)}</td>
                    <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
                        <ChevronRight size={16} color="var(--text-muted)" />
                        <RowMenu
                          label="Site actions"
                          onEdit={() => setDialog({ kind: 'site', mode: 'edit', site })}
                          onDelete={() => handleDeleteSite(site)}
                        />
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </section>

      {dialog && (
        <DetailDialog
          dialog={dialog}
          org={org}
          submitting={submitting}
          error={dialogError}
          onClose={() => {
            resetMutations()
            setDialog(null)
          }}
          onSubmit={handleDialogSubmit}
        />
      )}
    </main>
  )
}

function DetailDialog({ dialog, org, submitting, error, onClose, onSubmit }) {
  if (dialog.kind === 'org') {
    return <OrgDialog org={org} submitting={submitting} error={error} onClose={onClose} onSubmit={onSubmit} />
  }
  return <SiteDialog dialog={dialog} submitting={submitting} error={error} onClose={onClose} onSubmit={onSubmit} />
}

function OrgDialog({ org, submitting, error, onClose, onSubmit }) {
  const [name, setName] = useState(org?.name || '')
  const [slug, setSlug] = useState(org?.slug || '')
  const [type, setType] = useState(org?.type || 'other')
  const [slugTouched, setSlugTouched] = useState(true)

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const handleNameChange = (v) => {
    setName(v)
    if (!slugTouched) {
      setSlug(
        v.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-'),
      )
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!name.trim() || submitting) return
    onSubmit({ name: name.trim(), slug: slug || undefined, type })
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="org-dialog-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          <h2 id="org-dialog-title" className="modal__title">Edit organization</h2>
          <button type="button" className="icon-btn icon-btn--sm" aria-label="Close" onClick={onClose}>
            <X />
          </button>
        </div>
        <form className="modal__form" onSubmit={handleSubmit}>
          <label className="auth-field">
            <span className="auth-field__label">Name</span>
            <input
              autoFocus
              required
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
            />
          </label>
          <label className="auth-field">
            <span className="auth-field__label">Slug</span>
            <input
              value={slug}
              onChange={(e) => { setSlug(e.target.value); setSlugTouched(true) }}
              pattern="[a-z0-9-]*"
            />
          </label>
          <label className="auth-field">
            <span className="auth-field__label">Type</span>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              {ORG_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </label>
          {error && (
            <div className="auth-error" role="alert">
              <span>{error}</span>
            </div>
          )}
          <div className="modal__actions">
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn--primary" disabled={submitting || !name.trim()}>
              {submitting ? <><Loader2 size={14} className="spin" /> Saving...</> : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function SiteDialog({ dialog, submitting, error, onClose, onSubmit }) {
  const isEdit = dialog.mode === 'edit'
  const [values, setValues] = useState(() => ({
    name: dialog.site?.name || '',
    address: dialog.site?.address || '',
    timezone: dialog.site?.timezone || '',
  }))
  const [siteModel, setSiteModel] = useState(() =>
    initialModelForm(pickModelAsset(dialog.site)),
  )

  useEffect(() => {
    setValues({
      name: dialog.site?.name || '',
      address: dialog.site?.address || '',
      timezone: dialog.site?.timezone || '',
    })
    setSiteModel(initialModelForm(pickModelAsset(dialog.site)))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dialog.site is a new object each orgs fetch; id keeps the form stable while the modal is open
  }, [dialog.mode, dialog.site?.id])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const update = (field, value) => setValues((current) => ({ ...current, [field]: value }))

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!values.name.trim() || submitting) return
    onSubmit({
      name: values.name.trim(),
      address: values.address.trim(),
      timezone: values.timezone.trim(),
      siteModel,
    })
  }

  const patchSiteModel = (patch) => {
    setSiteModel((cur) => (typeof patch === 'function' ? patch(cur) : patch))
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="site-dialog-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          <h2 id="site-dialog-title" className="modal__title">
            {isEdit ? 'Edit site' : 'New site'}
          </h2>
          <button type="button" className="icon-btn icon-btn--sm" aria-label="Close" onClick={onClose}>
            <X />
          </button>
        </div>
        <form className="modal__form" onSubmit={handleSubmit}>
          <label className="auth-field">
            <span className="auth-field__label">Name</span>
            <input
              autoFocus
              required
              value={values.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="Sacramento VA Medical Center"
            />
          </label>
          <label className="auth-field">
            <span className="auth-field__label">Address</span>
            <input
              value={values.address}
              onChange={(e) => update('address', e.target.value)}
              placeholder="10535 Hospital Way, Mather, CA"
            />
          </label>
          <label className="auth-field">
            <span className="auth-field__label">Timezone</span>
            <TimezoneSelect
              value={values.timezone}
              onChange={(v) => update('timezone', v)}
            />
          </label>

          <ModelFormSection
            scope="site"
            entityId={dialog.site?.id || null}
            form={siteModel}
            onChange={patchSiteModel}
          />

          {error && (
            <div className="auth-error" role="alert">
              <span>{error}</span>
            </div>
          )}
          <div className="modal__actions">
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn--primary" disabled={submitting || !values.name.trim()}>
              {submitting ? (
                <><Loader2 size={14} className="spin" /> Saving...</>
              ) : isEdit ? 'Save changes' : 'Create site'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function typeLabel(value) {
  return ORG_TYPES.find((t) => t.value === value)?.label || value || 'Other'
}

function countFloors(org) {
  return (org.sites || []).reduce(
    (sum, site) => sum + countFloorsInSite(site),
    0,
  )
}

function countFloorsInSite(site) {
  return (site.buildings || []).reduce(
    (sum, b) => sum + (b.floors || []).length,
    0,
  )
}
